import { Component, inject, input, InputSignal, ViewChild } from '@angular/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { NotificationActions } from '@next/core-lib/notification';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { AppState, SprayTypes } from 'src/app/shared/models';

import { openBlightIrrigation } from '../../../store/actions';
import { SprayApplicationDialogService } from '@app/shared/spray-applications/services/spray-application-dialog.service';
import { BlightCropFieldAdvice } from '../../models/models';
import { BlightAIIdentifiers } from '../constants';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-blight-action-menu',
  template: `
    <div [matMenuTriggerFor]="actionMenu">
      <mat-menu #actionMenu="matMenu">
        <button mat-menu-item (click)="onSprayClicked()">
          {{ 'blightV2.actionMenu.spray' | translate }}
        </button>
        <mat-divider/>
        <button mat-menu-item (click)="onIrrigateClicked()">
          {{ 'blightV2.actionMenu.irrigation' | translate }}
        </button>
      </mat-menu>
    </div>
  `,
  imports: [MatMenuTrigger, MatMenu, MatMenuItem, MatDivider, TranslatePipe],
})
export class ActionMenuComponent {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly sprayApplicationDialogService = inject(SprayApplicationDialogService);
  private readonly locale = inject(LocaleService);

  @ViewChild('actionMenu', { static: true }) public menu!: MatMenu;

  @ViewChild(MatMenuTrigger) public menuTrigger!: MatMenuTrigger;

  protected sprayTypes: typeof SprayTypes = SprayTypes;

  readonly blightAdvices: InputSignal<BlightCropFieldAdvice[]> = input.required<BlightCropFieldAdvice[]>();
  readonly actionDate: InputSignal<Date | undefined> = input<Date | undefined>();

  protected onSprayClicked(): void {
    const cropYear = this.blightAdvices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackSprayEvent();
    this.sprayApplicationDialogService.openFromCropFields(
      'blight',
      this.blightAdvices(),
      SprayTypes.PreventiveSpray,
      this.actionDate(),
    );
  }

  protected onIrrigateClicked(): void {
    const cropYear = this.blightAdvices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackIrrigationEvent();
    this.store.dispatch(openBlightIrrigation({ advices: this.blightAdvices() }));
  }

  private trackSprayEvent(): void {
    this.ai.trackEvent(BlightAIIdentifiers.selectPreventive, {});
  }

  private trackIrrigationEvent(): void {
    this.ai.trackEvent(BlightAIIdentifiers.selectIrrigation, {});
  }

  private notifyMutationUnavailable(): void {
    this.store.dispatch(
      NotificationActions.infoSnackbar({
        message: this.locale.translate('UI.CropYearUnavailableMessage'),
      }),
    );
  }
}
